// Chatwoot support widget for the authenticated app (Oscar, the AI support
// bot, attaches later — AIT-237). Same contract as lib/firstpromoter.ts:
// SSR-safe, idempotent, no-op when the token is empty.
//
// Identity: setUser runs after the SDK is ready so conversations carry the
// real user email/name in Chatwoot instead of an anonymous pseudonym.
// D9: identifyChatwootUser fetches GET /support/chatwoot-identity for a
// signed `identifier_hash`, falling back to the unsigned setUser on any
// error (see applyIdentity below) so the widget never breaks.

const BASE_URL = 'https://app.chatwoot.com';

interface ChatwootSDK {
  run(opts: { websiteToken: string; baseUrl: string }): void;
}
interface ChatwootWidget {
  setUser(
    identifier: string,
    props: { email?: string; name?: string; identifier_hash?: string },
  ): void;
  reset(): void;
}

declare global {
  interface Window {
    chatwootSDK?: ChatwootSDK;
    chatwootSettings?: Record<string, unknown>;
    $chatwoot?: ChatwootWidget;
  }
}

let initialized = false;
// ponytail: module-level retry counter, not exponential backoff — a script
// load failure here is almost always a transient CDN/network/CSP blip, and
// this is a bounded best-effort widget, not a critical path.
let loadAttempts = 0;
const MAX_LOAD_ATTEMPTS = 3;

function loadWidget(token: string): void {
  loadAttempts += 1;
  const script = document.createElement('script');
  script.src = `${BASE_URL}/packs/js/sdk.js`;
  script.async = true;
  script.onload = () => {
    window.chatwootSDK?.run({ websiteToken: token, baseUrl: BASE_URL });
  };
  script.onerror = () => {
    script.remove();
    if (loadAttempts < MAX_LOAD_ATTEMPTS) {
      loadWidget(token);
    } else {
      // Give up for this mount; clear the guard + counter so a later
      // initChatwoot() call (e.g. a fresh page load) gets its own bounded
      // retry cycle instead of being permanently locked out.
      initialized = false;
      loadAttempts = 0;
    }
  };
  document.head.appendChild(script);
}

export function initChatwoot(): void {
  if (typeof window === 'undefined' || initialized) return;
  const token = import.meta.env.VITE_CHATWOOT_WEBSITE_TOKEN;
  if (!token) return;
  initialized = true;

  window.chatwootSettings = {
    position: 'right',
    type: 'expanded_bubble',
    // No AI promise until Oscar (AIT-237) actually answers here — plain
    // "Support" with the inbox's reply-time expectation setting the pace.
    launcherTitle: 'Support',
    darkMode: 'auto',
    welcomeTitle: 'Hi there 👋',
    welcomeDescription: 'Ask us anything about HookMyApp.',
    // No AI online yet (Oscar is AIT-237): set honest reply expectations.
    availableMessage: 'Leave a message — we reply within a few hours, and by email.',
    unavailableMessage: 'Leave a message — we reply within a few hours, and by email.',
  };
  loadWidget(token);
}

// Tracks the pending `chatwoot:ready` listener (if any) so a profile change
// or logout that races the widget's async load can cancel a stale identify
// call before it fires with the wrong user.
let pendingIdentify: (() => void) | null = null;

function clearPendingIdentify(): void {
  if (!pendingIdentify) return;
  window.removeEventListener('chatwoot:ready', pendingIdentify);
  pendingIdentify = null;
}

/** Matches `useFetch()`'s return type (frontend/src/hooks/use-fetch.ts). This
 * lib module has no access to the hook itself, so callers (layout
 * components) pass their own `fetchWithAuth` through. */
type FetchWithAuth = (
  path: string,
  options?: RequestInit,
  meta?: { suppressGlobalError?: boolean },
) => Promise<Response>;

// D9 — bumped on every identifyChatwootUser/resetChatwootIdentity call so an
// in-flight GET /support/chatwoot-identity from a superseded call (rapid
// profile change or logout) can't apply its stale result after the fact.
let identifyToken = 0;

async function applyIdentity(
  user: { publicId: string; email: string | null; firstName: string | null; lastName: string | null },
  fetchWithAuth: FetchWithAuth,
  token: number,
): Promise<void> {
  const props: { email?: string; name?: string; identifier_hash?: string } = {
    email: user.email ?? undefined,
    name: [user.firstName, user.lastName].filter(Boolean).join(' ') || undefined,
  };
  try {
    const res = await fetchWithAuth('/support/chatwoot-identity', undefined, {
      suppressGlobalError: true,
    });
    if (res.ok) {
      const data = (await res.json()) as { identifierHash?: unknown };
      if (typeof data.identifierHash === 'string') props.identifier_hash = data.identifierHash;
    }
  } catch {
    // Fail-soft (D9): identity validation is a nice-to-have signature on top
    // of the unsigned setUser call below — the widget must never break
    // because /support/chatwoot-identity is unreachable or unconfigured.
    // Deliberately silent, no console.warn — see spec D9.
  }
  if (token !== identifyToken) return; // superseded by a newer identify/reset call
  window.$chatwoot?.setUser(user.publicId, props);
}

export function identifyChatwootUser(
  user: {
    publicId: string;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
  },
  fetchWithAuth: FetchWithAuth,
): void {
  if (typeof window === 'undefined') return;
  clearPendingIdentify();
  const token = ++identifyToken;
  const identify = () => {
    pendingIdentify = null;
    void applyIdentity(user, fetchWithAuth, token);
  };
  if (window.$chatwoot) {
    identify();
  } else {
    pendingIdentify = identify;
    window.addEventListener('chatwoot:ready', identify, { once: true });
  }
}

/** Call when the authenticated user goes away (logout, profile → null) so the
 * widget doesn't keep the previous user's identity for the next SPA session. */
export function resetChatwootIdentity(): void {
  if (typeof window === 'undefined') return;
  clearPendingIdentify();
  identifyToken++;
  window.$chatwoot?.reset();
}
